import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@/shared/presentation/components/ui/Button';
import { DataTable } from '@/shared/presentation/components/ui/DataTable';
import { FormModal } from '@/shared/presentation/components/ui/FormModal';
import { Input } from '@/shared/presentation/components/ui/Input';
import { LoadingModal } from '@/shared/presentation/components/ui/LoadingDialog';
import { Page } from '@/shared/presentation/components/ui/Page';
import { TagInput } from '@/shared/presentation/components/ui/TagInput';
import { Textarea } from '@/shared/presentation/components/ui/Textarea';

import { OrganizationsViewModel } from './Organizations.vm';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const OrganizationForm = observer(({ isEditing }: { isEditing?: boolean }) => {
  const vm = useViewModel(OrganizationsViewModel);

  return (
    <Form>
      <Input
        label="Nome"
        name="name"
        errorMessage={vm.form.errors.name && vm.form.errors.name[0]}
        onChange={(value) => vm.form.setValue('name', value)}
        onBlur={() => vm.form.validateField('name')}
        value={vm.form.values.name}
        disabled={vm.isUpdating}
      />
      <Input
        label="Logo"
        name="logo"
        errorMessage={vm.form.errors.logo && vm.form.errors.logo[0]}
        onChange={(value) => vm.form.setValue('logo', value)}
        onBlur={() => vm.form.validateField('logo')}
        value={vm.form.values.logo}
        disabled={vm.isUpdating}
      />
      <Textarea
        label="Descrição"
        name="description"
        errorMessage={vm.form.errors.description && vm.form.errors.description[0]}
        onChange={(value) => vm.form.setValue('description', value)}
        onBlur={() => vm.form.validateField('description')}
        value={vm.form.values.description}
      />
      {isEditing && (
        <TagInput
          label="Aplicações"
          name="applications"
          isMulti={true}
          onChange={(values) => {
            vm.form.setValueAndValidateValue('applications', values);
          }}
          defaultValue={vm.form.values.applications}
          values={vm.form.values.applications}
          options={vm.applications.map((application) => ({
            guid: application.guid,
            name: application.name,
          }))}
        />
      )}
    </Form>
  );
});

const CreateOrganization = observer(() => {
  const vm = useViewModel(OrganizationsViewModel);

  return (
    <FormModal
      title="Adicionar organização"
      onClose={() => vm.closeCreateModal()}
      onSubmit={() => vm.create()}
      isSubmitting={vm.isSubmitting}
      isValid={vm.form.isValid}
    >
      <OrganizationForm />
    </FormModal>
  );
});

const CreatedOrganization = observer(() => {
  const vm = useViewModel(OrganizationsViewModel);

  return (
    <FormModal
      title="Organização criada com sucesso"
      onClose={() => vm.closeCreatedModal()}
      onSubmit={() => vm.openCreatedOrganization(vm.organization)}
      isValid={true}
      cancelButtonProps={{ label: 'Não' }}
      confirmButtonProps={{ label: 'Sim' }}
    >
      Deseja acessar a organização?
    </FormModal>
  );
});

const UpdateOrganization = observer(() => {
  const vm = useViewModel(OrganizationsViewModel);

  return (
    <FormModal
      title="Organização criada com sucesso"
      onClose={() => vm.closeUpdateModal()}
      onSubmit={() => vm.update()}
      isSubmitting={vm.isUpdating}
      isValid={vm.form.isValid}
    >
      <OrganizationForm isEditing />
    </FormModal>
  );
});

const NormalSpan = styled.span`
  white-space: normal;
`;

export const OrganizationsScreen = observer(() => {
  const vm = useViewModel(OrganizationsViewModel);

  const columns = useMemo(
    () => [
      {
        name: 'GUID',
        grow: 2,
        selector: (row) => row.guid,
        format: (row) => <NormalSpan>{row.guid}</NormalSpan>,
      },
      {
        name: 'Nome',
        selector: (row) => row.name,
        format: (row) => <Link to={`/admin/organizations/${row.guid}`}>{row.name}</Link>,
        sortable: true,
      },
      {
        name: 'Logo',
        center: true,
        grow: 1,
        selector: (row) => row.logo,
        format: (row) => <img style={{ height: '32px' }} src={row.logo} alt={row.logo} />,
      },
      {
        name: 'Descrição',
        selector: (row) => row.description || '',
      },
    ],
    [],
  );

  const actions = useMemo(
    () => [
      {
        name: 'Editar',
        onClick: (row) => {
          vm.openUpdateModal(row);
        },
      },
      {
        name: 'Excluir',
        onClick: (row) => {
          const confirm = window.confirm(
            'Tem certeza que deseja excluir? Nenhuma backup será realizado e a deleção é permanente',
          );
          if (confirm) {
            vm.remove(row.guid);
          }
        },
      },
    ],
    [],
  );

  return (
    <Page
      title="Organizações"
      subtitle="Gerencie as organizações do sistema"
      isLoading={vm.isLoading}
      hasError={vm.hasError}
      errorMessage={'Ocorreu um erro ao carregar as organizações.'}
      headerActions={() => (
        <Button icon="add" disabled={vm.isLoading} onClick={() => vm.openCreateModal()}>
          Adicionar
        </Button>
      )}
    >
      <DataTable
        isLoading={vm.isLoading}
        columns={columns}
        data={vm.organizations}
        actions={actions}
      />

      {vm.isCreateModalOpen && <CreateOrganization />}
      {vm.isCreatedModalOpen && <CreatedOrganization />}
      {vm.isUpdateModalOpen && <UpdateOrganization />}
      {vm.isRemoving && <LoadingModal text="Removendo organização..." />}
    </Page>
  );
});
